import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence, useScroll, useTransform } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import LanguageToggle from './LanguageToggle';
import '../../styles/Home.css';

export default function MainNavbar() {
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [scrolled, setScrolled] = useState(false);
    const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
    const { scrollY } = useScroll();
    const navigate = useNavigate();
    const userMenuRef = useRef<HTMLDivElement>(null);
    // @ts-ignore — react-i18next v13 types need TS 5.0+
    const { t } = useTranslation();

    // Auth context
    const { user, isAuthenticated, logout, isLoading } = useAuth();

    // Close user menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
                setIsUserMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Damru shake effect based on scroll velocity
    const damruRotate = useTransform(scrollY, (latest) => {
        const velocity = latest - (scrollY.getPrevious() || 0);
        return velocity * 0.5;
    });

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 20);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Keyboard shortcut for search (Ctrl/Cmd + K)
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
                e.preventDefault();
                setIsSearchOpen(true);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    const handleSearch = () => {
        if (searchQuery.trim()) {
            navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
            setIsSearchOpen(false);
            setSearchQuery('');
        }
    };

    return (
        <motion.nav
            initial={{ y: -100 }}
            animate={{ y: 0 }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
            className={`fixed top-0 left-0 right-0 z-50 transition-shadow duration-500 bg-white ${
                scrolled ? 'shadow-md' : 'shadow-sm'
            }`}
            style={{ borderBottom: '1px solid #1a1a2e' }}
        >
            {/* Floating spiritual symbols at edges */}
            <div className="navbar-edge-symbols navbar-edge-symbols-left">
                <span className="edge-symbol" style={{ left: '20%', top: '15%', fontSize: 14, color: '#b8860b', animationDelay: '0s' }}>ॐ</span>
                <span className="edge-symbol" style={{ left: '55%', top: '45%', animationDelay: '1.8s' }}>
                    <svg width="14" height="18" viewBox="0 0 16 22" fill="none"><path d="M8 20V10M8 10V2M4 10L2 3M12 10L14 3M4 10H12" stroke="#7c3aed" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </span>
                <span className="edge-symbol" style={{ left: '35%', top: '70%', animationDelay: '3.5s' }}>
                    <svg width="12" height="15" viewBox="0 0 18 22" fill="none"><path d="M3 2L15 2L9 11L15 20L3 20L9 11Z" stroke="#db2777" strokeWidth="1.3" strokeLinejoin="round"/></svg>
                </span>
            </div>
            <div className="navbar-edge-symbols navbar-edge-symbols-right">
                <span className="edge-symbol" style={{ right: '25%', top: '20%', animationDelay: '1s' }}>
                    <svg width="14" height="18" viewBox="0 0 16 22" fill="none"><path d="M8 20V10M8 10V2M4 10L2 3M12 10L14 3M4 10H12" stroke="#b8860b" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </span>
                <span className="edge-symbol" style={{ right: '50%', top: '55%', fontSize: 14, color: '#7c3aed', animationDelay: '2.8s' }}>ॐ</span>
                <span className="edge-symbol" style={{ right: '30%', top: '75%', animationDelay: '4.2s' }}>
                    <svg width="12" height="15" viewBox="0 0 18 22" fill="none"><path d="M3 2L15 2L9 11L15 20L3 20L9 11Z" stroke="#db2777" strokeWidth="1.3" strokeLinejoin="round"/></svg>
                </span>
            </div>

            <div className="relative z-[2] max-w-7xl mx-auto px-4 md:px-6 py-3 md:py-4">
                <div className="flex items-center justify-between">
                    {/* Logo Section */}
                    <Link
                        to="/"
                        className="flex items-center space-x-2 min-[400px]:space-x-3 group relative"
                        aria-label="Home"
                    >
                        {/* Animated Logo Container */}
                        <motion.div
                            style={{ rotate: damruRotate }}
                            className="relative w-10 h-10 min-[400px]:w-12 min-[400px]:h-12 md:w-14 md:h-14 flex items-center justify-center"
                        >
                            {/* Glow effect on hover */}
                            <motion.div
                                className="absolute inset-0 rounded-full blur-xl"
                                style={{ background: 'rgba(184, 134, 11, 0.15)' }}
                                initial={{ opacity: 0, scale: 0.8 }}
                                whileHover={{ opacity: 1, scale: 1.2 }}
                                transition={{ duration: 0.3 }}
                            />

                            {/* Logo Image */}
                            <motion.img
                                src="/images/logo.webp"
                                alt="Bhairav Aaradhyaa Logo"
                                loading="eager"
                                className="relative z-10 w-full h-full object-contain drop-shadow-lg"
                                whileHover={{ scale: 1.1 }}
                                transition={{ duration: 0.3 }}
                            />
                        </motion.div>

                        {/* Brand Text - More golden, slower animation */}
                        <div className="flex flex-col relative overflow-hidden">
                            <motion.span
                                className="relative whitespace-nowrap text-lg min-[400px]:text-xl md:text-2xl font-bold font-dancing tracking-wide"
                                style={{
                                    background: 'linear-gradient(135deg, #b8860b 0%, #ffd700 40%, #b8860b 60%, #ffd700 100%)',
                                    backgroundSize: '300% 100%',
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                    backgroundClip: 'text',
                                    animation: 'navGoldenShine 6s ease-in-out infinite',
                                    filter: 'drop-shadow(0 1px 2px rgba(184, 134, 11, 0.3))',
                                }}
                                whileHover={{ scale: 1.02 }}
                            >
                                Bhairav Aaradhyaa
                            </motion.span>

                            <motion.span
                                className="relative text-xs md:text-sm font-noto-devanagari -mt-1"
                                style={{
                                    color: '#b8860b',
                                    textShadow: '0 0 6px rgba(184, 134, 11, 0.2)',
                                }}
                                initial={{ opacity: 0.9 }}
                                whileHover={{ opacity: 1 }}
                            >
                                भैरव आराध्या
                            </motion.span>
                        </div>

                        {/* Hover glow effect */}
                        <motion.div
                            className="absolute -inset-2 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                            style={{
                                background: 'radial-gradient(circle at center, rgba(184, 134, 11, 0.08) 0%, transparent 70%)',
                            }}
                        />
                    </Link>

                    {/* Search & Auth Section - Universal theme styled */}
                    <div className="flex items-center space-x-2 min-[400px]:space-x-3">
                        <AnimatePresence mode="wait">
                            {!isSearchOpen ? (
                                <motion.button
                                    key="search-button"
                                    initial={{ opacity: 0, scale: 0.8 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0, scale: 0.8 }}
                                    whileHover={{ scale: 1.1 }}
                                    whileTap={{ scale: 0.9 }}
                                    onClick={() => setIsSearchOpen(true)}
                                    className="relative p-2 min-[400px]:p-2.5 rounded-full transition-colors duration-300 group"
                                    style={{
                                        border: '2px solid transparent',
                                        background: 'linear-gradient(white, white) padding-box, linear-gradient(135deg, #b8860b, #7c3aed, #db2777) border-box',
                                        color: '#b8860b',
                                    }}
                                    aria-label="Open search"
                                >
                                    {/* Hover gradient fill */}
                                    <span
                                        className="absolute inset-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                                        style={{ background: 'linear-gradient(135deg, rgba(184,134,11,0.1), rgba(124,58,237,0.1), rgba(219,39,119,0.1))' }}
                                    />
                                    <svg
                                        className="w-5 h-5 relative z-10 group-hover:text-purple-700 transition-colors duration-300"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth="2.5"
                                            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                                        />
                                    </svg>
                                </motion.button>
                            ) : (
                                <motion.div
                                    key="search-bar"
                                    initial={{ opacity: 0, width: 0, x: 50 }}
                                    animate={{ opacity: 1, width: 'auto', x: 0 }}
                                    exit={{ opacity: 0, width: 0, x: 50 }}
                                    transition={{ duration: 0.4, ease: 'easeOut' }}
                                    className="flex items-center rounded-full px-5 py-2.5 shadow-lg"
                                    style={{
                                        border: '2px solid transparent',
                                        background: 'linear-gradient(white, white) padding-box, linear-gradient(135deg, #b8860b, #7c3aed, #db2777) border-box',
                                    }}
                                >
                                    <svg
                                        className="w-4 h-4 mr-3"
                                        style={{ color: '#b8860b' }}
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth="2"
                                            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                                        />
                                    </svg>

                                    <input
                                        type="text"
                                        placeholder={t('search.placeholder') as string}
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') handleSearch();
                                            if (e.key === 'Escape') setIsSearchOpen(false);
                                        }}
                                        className="bg-transparent text-charcoal placeholder-charcoal/40 outline-none w-36 md:w-64 text-sm font-noto-serif"
                                        autoFocus
                                    />

                                    <motion.button
                                        onClick={handleSearch}
                                        whileHover={{ scale: 1.1 }}
                                        whileTap={{ scale: 0.95 }}
                                        className="ml-3 transition-colors"
                                        style={{ color: '#7c3aed' }}
                                        aria-label="Search"
                                    >
                                        <svg
                                            className="w-5 h-5"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth="2"
                                                d="M13 7l5 5m0 0l-5 5m5-5H6"
                                            />
                                        </svg>
                                    </motion.button>

                                    <motion.button
                                        onClick={() => setIsSearchOpen(false)}
                                        whileHover={{ scale: 1.1, rotate: 90 }}
                                        whileTap={{ scale: 0.95 }}
                                        className="ml-2 transition-colors"
                                        style={{ color: '#db2777' }}
                                        aria-label="Close search"
                                    >
                                        <svg
                                            className="w-5 h-5"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth="2"
                                                d="M6 18L18 6M6 6l12 12"
                                            />
                                        </svg>
                                    </motion.button>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        {/* Login/Account Button */}
                        {!isLoading && (
                            <div className="relative" ref={userMenuRef}>
                                {isAuthenticated && user ? (
                                    <>
                                        <motion.button
                                            whileHover={{ scale: 1.05 }}
                                            whileTap={{ scale: 0.95 }}
                                            onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                                            className="relative p-2 rounded-full transition-colors duration-300 group flex items-center gap-2"
                                            style={{
                                                border: '2px solid transparent',
                                                background: 'linear-gradient(white, white) padding-box, linear-gradient(135deg, #b8860b, #7c3aed, #db2777) border-box',
                                            }}
                                            aria-label="Account menu"
                                        >
                                            <span
                                                className="absolute inset-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                                                style={{ background: 'linear-gradient(135deg, rgba(184,134,11,0.1), rgba(124,58,237,0.1), rgba(219,39,119,0.1))' }}
                                            />
                                            <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-sm font-bold relative z-10"
                                                style={{ background: 'linear-gradient(135deg, #b8860b, #7c3aed)' }}
                                            >
                                                {user.firstName.charAt(0).toUpperCase()}
                                            </div>
                                            <span className="hidden md:block text-sm font-medium text-gray-700 relative z-10 pr-1">
                                                {user.firstName}
                                            </span>
                                        </motion.button>

                                        {/* Dropdown Menu */}
                                        <AnimatePresence>
                                            {isUserMenuOpen && (
                                                <motion.div
                                                    initial={{ opacity: 0, y: -10, scale: 0.95 }}
                                                    animate={{ opacity: 1, y: 0, scale: 1 }}
                                                    exit={{ opacity: 0, y: -10, scale: 0.95 }}
                                                    transition={{ duration: 0.2 }}
                                                    className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-xl overflow-hidden z-50"
                                                    style={{
                                                        border: '2px solid transparent',
                                                        background: 'linear-gradient(white, white) padding-box, linear-gradient(135deg, #b8860b, #7c3aed, #db2777) border-box',
                                                    }}
                                                >
                                                    <div className="px-4 py-3 border-b border-gray-100">
                                                        <p className="text-sm font-semibold text-gray-800">{user.firstName} {user.lastName}</p>
                                                        <p className="text-xs text-gray-500 truncate">{user.email}</p>
                                                    </div>
                                                    <div className="py-1">
                                                        <Link
                                                            to="/account"
                                                            onClick={() => setIsUserMenuOpen(false)}
                                                            className="flex items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-golden/5 transition-colors"
                                                        >
                                                            <svg className="w-4 h-4 mr-3 text-golden" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                                            </svg>
                                                            {t('auth.myAccount')}
                                                        </Link>
                                                        <Link
                                                            to="/bhairav/naam-japa"
                                                            onClick={() => setIsUserMenuOpen(false)}
                                                            className="flex items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-golden/5 transition-colors"
                                                        >
                                                            <svg className="w-4 h-4 mr-3 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                                                <circle cx="12" cy="9.5" r="6.5" strokeWidth={1} />
                                                                <g fill="currentColor" stroke="none">
                                                                    <circle cx="12" cy="3" r="1.25" />
                                                                    <circle cx="16.6" cy="4.9" r="1.25" />
                                                                    <circle cx="18.5" cy="9.5" r="1.25" />
                                                                    <circle cx="16.6" cy="14.1" r="1.25" />
                                                                    <circle cx="7.4" cy="14.1" r="1.25" />
                                                                    <circle cx="5.5" cy="9.5" r="1.25" />
                                                                    <circle cx="7.4" cy="4.9" r="1.25" />
                                                                    <circle cx="12" cy="16" r="1.9" />
                                                                </g>
                                                                <path d="M12 18.1v3.4M10.7 21.5h2.6" />
                                                            </svg>
                                                            {t('auth.naamJapaCounter')}
                                                        </Link>
                                                        <Link
                                                            to="/bhairav/life-purpose"
                                                            onClick={() => setIsUserMenuOpen(false)}
                                                            className="flex items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-golden/5 transition-colors"
                                                        >
                                                            <svg className="w-4 h-4 mr-3 text-pink-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                                                <circle cx="12" cy="12" r="9.5" />
                                                                <polygon points="16.2 7.8 14.1 14.1 7.8 16.2 9.9 9.9 16.2 7.8" />
                                                            </svg>
                                                            {t('auth.lifePurpose')}
                                                        </Link>
                                                    </div>
                                                    <div className="border-t border-gray-100 py-1">
                                                        <button
                                                            onClick={() => { logout(); setIsUserMenuOpen(false); }}
                                                            className="flex items-center w-full px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
                                                        >
                                                            <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                                                            </svg>
                                                            {t('auth.signOut')}
                                                        </button>
                                                    </div>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </>
                                ) : (
                                    <Link to="/login">
                                        <motion.button
                                            whileHover={{ scale: 1.05 }}
                                            whileTap={{ scale: 0.95 }}
                                            className="relative p-2 min-[400px]:p-2.5 rounded-full transition-colors duration-300 group"
                                            style={{
                                                border: '2px solid transparent',
                                                background: 'linear-gradient(white, white) padding-box, linear-gradient(135deg, #b8860b, #7c3aed, #db2777) border-box',
                                                color: '#b8860b',
                                            }}
                                            aria-label="Sign in"
                                        >
                                            <span
                                                className="absolute inset-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                                                style={{ background: 'linear-gradient(135deg, rgba(184,134,11,0.1), rgba(124,58,237,0.1), rgba(219,39,119,0.1))' }}
                                            />
                                            <svg
                                                className="w-5 h-5 relative z-10 group-hover:text-purple-700 transition-colors duration-300"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                            </svg>
                                        </motion.button>
                                    </Link>
                                )}
                            </div>
                        )}

                        {/* Language Toggle - after profile */}
                        <LanguageToggle />
                    </div>
                </div>
            </div>

            {/* Inline keyframes for slower golden shine */}
            <style>{`
                @keyframes navGoldenShine {
                    0% { background-position: 0% 50%; }
                    50% { background-position: 100% 50%; }
                    100% { background-position: 0% 50%; }
                }
            `}</style>
        </motion.nav>
    );
}